'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../contexts/theme-context';
import { Select } from '../select';
const AdaptiveFormContext = createContext(null);
export function AdaptiveForm({ children, onSubmit, layout = 'vertical', fieldSize = 'medium', className = '', id, enableKeyboardNavigation = true, gap = 'medium' }) {
    const formRef = useRef(null);
    const { theme } = useTheme();
    // Use direct CSS variable checking as fallback + theme context
    const [density, setDensity] = useState(() => {
        if (typeof window !== 'undefined') {
            const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--layout-density');
            return cssVar.trim() === 'compact' ? 'compact' : 'standard';
        }
        return theme?.layoutDensity === 'compact' ? 'compact' : 'standard';
    });
    const [forceUpdate, setForceUpdate] = useState(0);
    // Watch for CSS variable changes (more reliable than theme context)
    useEffect(() => {
        const checkDensity = () => {
            if (typeof window === 'undefined')
                return;
            const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--layout-density');
            const cssVarDensity = cssVar.trim() === 'compact' ? 'compact' : 'standard';
            const themeDensity = theme?.layoutDensity === 'compact' ? 'compact' : 'standard';
            // Use CSS variable as primary source, theme as fallback
            const newDensity = cssVar ? cssVarDensity : themeDensity;
            if (newDensity !== density) {
                setDensity(newDensity);
                setForceUpdate(prev => prev + 1);
            }
        };
        // Check immediately
        checkDensity();
        // Set up mutation observer to watch for CSS variable changes
        const observer = new MutationObserver(() => {
            setTimeout(checkDensity, 100); // Slight delay to allow CSS to update
        });
        if (document.documentElement) {
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['style', 'class']
            });
        }
        return () => observer.disconnect();
    }, [theme, density]);
    // Handle keyboard navigation
    const handleFieldKeyDown = (e, fieldIndex) => {
        if (!enableKeyboardNavigation)
            return;
        const form = formRef.current;
        if (!form)
            return;
        const allFields = form.querySelectorAll('input, select, textarea, [role="combobox"]');
        const totalFields = allFields.length;
        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                if (fieldIndex < totalFields - 1) {
                    const nextField = allFields[fieldIndex + 1];
                    nextField?.focus();
                }
                break;
            case 'ArrowDown':
                if (layout === 'horizontal' || layout === 'vertical') {
                    e.preventDefault();
                    if (fieldIndex < totalFields - 1) {
                        const nextField = allFields[fieldIndex + 1];
                        nextField?.focus();
                    }
                }
                break;
            case 'ArrowUp':
                if (layout === 'horizontal' || layout === 'vertical') {
                    e.preventDefault();
                    if (fieldIndex > 0) {
                        const prevField = allFields[fieldIndex - 1];
                        prevField?.focus();
                    }
                }
                break;
            case 'ArrowRight':
                if (layout === 'inline' && fieldIndex < totalFields - 1) {
                    e.preventDefault();
                    const nextField = allFields[fieldIndex + 1];
                    nextField?.focus();
                }
                break;
            case 'ArrowLeft':
                if (layout === 'inline' && fieldIndex > 0) {
                    e.preventDefault();
                    const prevField = allFields[fieldIndex - 1];
                    prevField?.focus();
                }
                break;
        }
    };
    // Get layout-specific classes
    const getLayoutClasses = () => {
        const gapClasses = {
            none: 'gap-0',
            small: density === 'compact' ? 'gap-1' : 'gap-2',
            medium: density === 'compact' ? 'gap-2' : 'gap-4',
            large: density === 'compact' ? 'gap-3' : 'gap-6'
        };
        let layoutClasses = '';
        switch (layout) {
            case 'single-column':
                layoutClasses = `flex flex-col ${gapClasses[gap]}`;
                break;
            case 'two-column':
                layoutClasses = `grid grid-cols-1 md:grid-cols-2 ${gapClasses[gap]}`;
                break;
            case 'horizontal':
                layoutClasses = `flex flex-col ${density === 'compact' ? 'gap-px' : 'gap-4'}`;
                break;
            case 'vertical':
                layoutClasses = `flex flex-col ${gapClasses[gap]}`;
                break;
            case 'inline':
                layoutClasses = `flex flex-wrap items-end ${gapClasses[gap]}`;
                break;
            case 'card-grid':
                layoutClasses = `grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${gapClasses[gap]}`;
                break;
            default:
                layoutClasses = `flex flex-col ${gapClasses[gap]}`;
        }
        return layoutClasses;
    };
    const contextValue = {
        handleFieldKeyDown,
        formRef,
        layout,
        density,
        fieldSize
    };
    return (_jsx(AdaptiveFormContext.Provider, { value: contextValue, children: _jsx("form", { ref: formRef, onSubmit: onSubmit, className: cn(getLayoutClasses(), className), id: id, children: children }) }, `form-${density}-${layout}-${forceUpdate}`));
}
export function AdaptiveField({ label, name, type = 'text', placeholder, required = false, value, onChange, fieldIndex, autoFocus = false, disabled = false, className = '', labelWidth, helperText, error, rows = 3, colSpan = 1, ...props }) {
    const context = useContext(AdaptiveFormContext);
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    if (!context) {
        throw new Error('AdaptiveField must be used within an AdaptiveForm');
    }
    const { handleFieldKeyDown, layout, density, fieldSize } = context;
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);
    const handleKeyDown = (e) => {
        handleFieldKeyDown(e, fieldIndex);
    };
    // Get field size classes based on density and fieldSize
    const getFieldSizeClasses = () => {
        if (density === 'compact') {
            return {
                input: 'h-10 px-2 text-xs',
                textarea: 'px-2 py-1 text-xs min-h-[2.5rem]',
                label: 'text-xs',
                helper: 'text-xs'
            };
        }
        const sizeMap = {
            small: {
                input: 'h-9 px-3 text-sm',
                textarea: 'px-3 py-2 text-sm min-h-[2.5rem]',
                label: 'text-sm',
                helper: 'text-sm'
            },
            medium: {
                input: 'h-10 px-3 text-sm',
                textarea: 'px-3 py-2 text-sm min-h-[3rem]',
                label: 'text-sm',
                helper: 'text-sm'
            },
            large: {
                input: 'h-12 px-4 text-base',
                textarea: 'px-4 py-3 text-base min-h-[4rem]',
                label: 'text-base',
                helper: 'text-sm'
            }
        };
        return sizeMap[fieldSize];
    };
    const sizeClasses = getFieldSizeClasses();
    // Get layout-specific wrapper classes
    const getWrapperClasses = () => {
        const baseClasses = colSpan === 2 ? 'col-span-2' : '';
        const spacingClass = 'space-y-1'; // Match Select component spacing
        switch (layout) {
            case 'horizontal':
                return `${baseClasses} flex flex-col md:flex-row md:items-start ${density === 'compact' ? 'mb-px pb-1' : 'mb-4'}`;
            case 'inline':
                return `${baseClasses} flex-1 min-w-0`;
            case 'card-grid':
                return `${baseClasses} ${spacingClass} p-4 border border-gray-200 dark:border-gray-700 rounded-lg`;
            default:
                return `${baseClasses} ${spacingClass}`;
        }
    };
    // Get label classes
    const getLabelClasses = () => {
        const baseClasses = `font-medium text-gray-700 dark:text-gray-300 transition-colors duration-150 ${sizeClasses.label}`;
        const focusClasses = isFocused ? 'text-primary-700 dark:text-primary-400' : '';
        switch (layout) {
            case 'horizontal':
                const width = labelWidth || (density === 'compact' ? 'w-32' : 'w-48');
                return `${width} ${baseClasses} ${focusClasses} text-right ${density === 'compact' ? 'pr-3 pt-1' : 'pr-4 pt-2'} flex-shrink-0`;
            case 'inline':
                return `${baseClasses} ${focusClasses} block`;
            default:
                return `${baseClasses} ${focusClasses} block`;
        }
    };
    // Get input classes
    const getInputClasses = () => {
        const sizeClass = type === 'textarea' ? sizeClasses.textarea : sizeClasses.input;
        const baseClasses = `w-full border rounded transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${sizeClass}`;
        const stateClasses = cn(isFocused
            ? 'border-primary-500 ring-1 ring-primary-200 dark:ring-primary-800 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800', disabled && 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed', error && 'border-red-500 focus:border-red-500 focus:ring-red-200', !disabled && !error && 'dark:text-gray-100', (type === 'date' || type === 'datetime-local' || type === 'time') && !disabled && 'cursor-pointer');
        const layoutClasses = '';
        const finalClasses = cn(baseClasses, stateClasses, layoutClasses, className);
        return finalClasses;
    };
    // Get field container width for horizontal layout
    const getFieldWidth = () => {
        if (layout === 'horizontal') {
            return 'flex-1 min-w-0';
        }
        return 'w-full';
    };
    return (_jsxs("div", { className: getWrapperClasses(), children: [_jsxs("label", { className: getLabelClasses(), children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] }), _jsxs("div", { className: getFieldWidth(), children: [type === 'checkbox' ? (_jsxs("div", { className: "flex items-center", children: [_jsx("input", { ref: inputRef, type: "checkbox", name: name, checked: value || false, onChange: onChange, onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, disabled: disabled, className: "w-4 h-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:ring-2 bg-white dark:bg-gray-700 cursor-pointer", ...props }), helperText && (_jsx("span", { className: `ml-2 text-gray-600 dark:text-gray-400 ${sizeClasses.helper}`, children: helperText }))] })) : type === 'textarea' ? (_jsx("textarea", { ref: inputRef, name: name, value: value || '', onChange: onChange, placeholder: placeholder, rows: rows, className: getInputClasses(), onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, disabled: disabled, ...props })) : (_jsx("input", { ref: inputRef, type: type, name: name, value: value ?? '', onChange: onChange, placeholder: placeholder, className: getInputClasses(), onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, disabled: disabled, ...props })), helperText && type !== 'checkbox' && (_jsx("p", { className: `mt-1 text-gray-600 dark:text-gray-400 ${sizeClasses.helper}`, children: helperText })), error && (_jsx("p", { className: `mt-1 text-red-600 dark:text-red-400 ${sizeClasses.helper}`, children: error }))] })] }));
}
export function AdaptiveSelectField({ label, name: _name, options, value, onChange, fieldIndex: _fieldIndex, placeholder = 'Select...', required = false, disabled = false, isSearchable = false, isClearable = false, isMulti = false, className = '', helperText, error, colSpan = 1 }) {
    const context = useContext(AdaptiveFormContext);
    const [isFocused, setIsFocused] = useState(false);
    if (!context) {
        throw new Error('AdaptiveSelectField must be used within an AdaptiveForm');
    }
    const { layout, density, fieldSize } = context;
    // Get field size classes
    const getFieldSizeClasses = () => {
        if (density === 'compact') {
            return {
                label: 'text-xs',
                helper: 'text-xs',
                control: {
                    minHeight: '40px',
                    height: '40px',
                    fontSize: '12px'
                }
            };
        }
        const sizeMap = {
            small: {
                label: 'text-sm',
                helper: 'text-sm',
                control: { minHeight: '36px', height: '36px', fontSize: '14px' }
            },
            medium: {
                label: 'text-sm',
                helper: 'text-sm',
                control: { minHeight: '40px', height: '40px', fontSize: '14px' }
            },
            large: {
                label: 'text-base',
                helper: 'text-sm',
                control: { minHeight: '48px', height: '48px', fontSize: '16px' }
            }
        };
        return sizeMap[fieldSize];
    };
    const sizeClasses = getFieldSizeClasses();
    // Get layout-specific wrapper classes
    const getWrapperClasses = () => {
        const baseClasses = colSpan === 2 ? 'col-span-2' : '';
        const spacingClass = 'space-y-1'; // Match Select component spacing
        switch (layout) {
            case 'horizontal':
                return `${baseClasses} flex flex-col md:flex-row md:items-start ${density === 'compact' ? 'mb-px pb-1' : 'mb-4'}`;
            case 'inline':
                return `${baseClasses} flex-1 min-w-0`;
            case 'card-grid':
                return `${baseClasses} ${spacingClass} p-4 border border-gray-200 dark:border-gray-700 rounded-lg`;
            default:
                return `${baseClasses} ${spacingClass}`;
        }
    };
    // Get label classes
    const getLabelClasses = () => {
        const baseClasses = `font-medium text-gray-700 dark:text-gray-300 transition-colors duration-150 ${sizeClasses.label}`;
        const focusClasses = isFocused ? 'text-primary-700 dark:text-primary-400' : '';
        switch (layout) {
            case 'horizontal':
                const width = density === 'compact' ? 'w-32' : 'w-48';
                return `${width} ${baseClasses} ${focusClasses} text-right ${density === 'compact' ? 'pr-3 py-1' : 'pr-4 py-2'} flex-shrink-0`;
            case 'inline':
                return `${baseClasses} ${focusClasses} block`;
            default:
                return `${baseClasses} ${focusClasses} block`;
        }
    };
    // Get field container width
    const getFieldWidth = () => {
        if (layout === 'horizontal') {
            return density === 'compact' ? 'w-full md:w-[35%]' : 'w-full md:flex-1';
        }
        return 'w-full';
    };
    return (_jsxs("div", { className: getWrapperClasses(), children: [_jsx("label", { className: getLabelClasses(), children: label }), _jsxs("div", { className: getFieldWidth(), children: [_jsx(Select, { placeholder: placeholder, options: options, value: value, onChange: onChange, className: cn('text-xs', density === 'compact' ? 'adaptive-select-compact' : 'adaptive-select-standard', className), classNamePrefix: "adaptive-select", styles: {
                            control: (base, state) => ({
                                ...base,
                                minHeight: sizeClasses.control.minHeight,
                                height: sizeClasses.control.height,
                                fontSize: sizeClasses.control.fontSize,
                                borderColor: state.isFocused ? '#3B82F6' : base.borderColor,
                                boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : base.boxShadow,
                                // Force override any default React Select heights
                                '&': {
                                    minHeight: `${sizeClasses.control.minHeight} !important`,
                                    height: `${sizeClasses.control.height} !important`
                                }
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: sizeClasses.control.height,
                                minHeight: sizeClasses.control.minHeight,
                                padding: density === 'compact' ? '0 6px' : '0 12px',
                                display: 'flex',
                                alignItems: 'center'
                            }),
                            input: (base) => ({
                                ...base,
                                margin: '0px',
                                fontSize: sizeClasses.control.fontSize
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: sizeClasses.control.height,
                                minHeight: sizeClasses.control.minHeight,
                                padding: density === 'compact' ? '0 4px' : '0 8px',
                                display: 'flex',
                                alignItems: 'center'
                            }),
                            indicatorSeparator: (base) => ({
                                ...base,
                                height: density === 'compact' ? '12px' : '20px',
                                marginTop: density === 'compact' ? '6px' : '10px'
                            }),
                            dropdownIndicator: (base) => ({
                                ...base,
                                padding: density === 'compact' ? '2px' : '8px',
                                fontSize: sizeClasses.control.fontSize
                            }),
                            placeholder: (base) => ({
                                ...base,
                                fontSize: sizeClasses.control.fontSize
                            }),
                            singleValue: (base) => ({
                                ...base,
                                fontSize: sizeClasses.control.fontSize
                            })
                        }, isSearchable: isSearchable, isClearable: isClearable, isMulti: isMulti, isDisabled: disabled, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false) }), helperText && (_jsx("p", { className: `mt-1 text-gray-600 dark:text-gray-400 ${sizeClasses.helper}`, children: helperText })), error && (_jsx("p", { className: `mt-1 text-red-600 dark:text-red-400 ${sizeClasses.helper}`, children: error }))] })] }));
}
export function AdaptiveFormSection({ title, description, children, className = '', collapsible = false, defaultExpanded = true }) {
    const context = useContext(AdaptiveFormContext);
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    if (!context) {
        throw new Error('AdaptiveFormSection must be used within an AdaptiveForm');
    }
    const { density } = context;
    const getSectionClasses = () => {
        return density === 'compact' ? 'mb-3' : 'mb-6';
    };
    const getTitleClasses = () => {
        return density === 'compact'
            ? 'text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-2 pb-1 border-b border-gray-200 dark:border-gray-700'
            : 'text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-3 pb-2 border-b border-gray-200 dark:border-gray-700';
    };
    return (_jsxs("div", { className: cn(getSectionClasses(), className), children: [_jsxs("div", { className: cn(getTitleClasses(), collapsible && 'cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 flex items-center justify-between'), onClick: collapsible ? () => setIsExpanded(!isExpanded) : undefined, children: [_jsxs("div", { children: [title, description && (_jsx("p", { className: `mt-1 font-normal text-gray-600 dark:text-gray-400 normal-case tracking-normal ${density === 'compact' ? 'text-xs' : 'text-sm'}`, children: description }))] }), collapsible && (_jsx("svg", { className: cn('w-4 h-4 transition-transform duration-200', isExpanded && 'rotate-180'), fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" }) }))] }), (!collapsible || isExpanded) && (_jsx("div", { className: density === 'compact' ? 'space-y-0' : 'space-y-2', children: children }))] }));
}
// KeyboardHints component for showing navigation shortcuts
export function AdaptiveFormKeyboardHints({ className = '' }) {
    const context = useContext(AdaptiveFormContext);
    if (!context)
        return null;
    const { layout, density } = context;
    const getHints = () => {
        switch (layout) {
            case 'horizontal':
            case 'vertical':
                return [
                    { key: '↵ Enter', action: 'Next Field' },
                    { key: '↑↓', action: 'Navigate' }
                ];
            case 'inline':
                return [
                    { key: '→←', action: 'Navigate' },
                    { key: '↵ Enter', action: 'Next Field' }
                ];
            default:
                return [
                    { key: '↵ Enter', action: 'Next Field' }
                ];
        }
    };
    return (_jsx("div", { className: cn(`flex items-center gap-4 text-gray-500 dark:text-gray-400 ${density === 'compact' ? 'text-xs' : 'text-sm'}`, className), children: getHints().map((hint, index) => (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "font-semibold", children: hint.key }), _jsx("span", { children: "=" }), _jsx("span", { children: hint.action })] }, index))) }));
}
